import { FrontendApplicationContribution, Saveable, WidgetManager } from '@theia/core/lib/browser';
import { IconThemeService } from '@theia/core/lib/browser/icon-theme-service';
import { ThemeService } from '@theia/core/lib/browser/theming';
import {
    PreferenceService,
    PreferenceService as PreferenceServiceSymbol
} from '@theia/core/lib/common/preferences/preference-service';
import { inject, injectable } from '@theia/core/shared/inversify';
import { PANEL_FILE_ICON_THEME_ID } from './file-icon-theme/file-icon-data';

const PANEL_DARK_ID: string = 'lvgl-dark';
const PANEL_LIGHT_ID: string = 'lvgl-light';
const OPEN_EDITORS_WIDGET_ID = 'theia-open-editors-widget';

@injectable()
export class Contribution implements FrontendApplicationContribution {
    @inject(ThemeService)
    protected readonly themeService!: ThemeService;

    @inject(IconThemeService)
    protected readonly iconThemeService!: IconThemeService;

    @inject(PreferenceServiceSymbol)
    protected readonly preferences!: PreferenceService;

    @inject(WidgetManager)
    protected readonly widgetManager!: WidgetManager;

    protected navigatorDecoratorPatched: boolean = false;
    protected canceledRejectionHandlerInstalled: boolean = false;
    protected invalidKeydownGuardInstalled: boolean = false;

    onStart(): void {
        this.installCanceledRejectionHandler();
        this.installInvalidKeydownGuard();
        this.patchNavigatorTabBarDecorator();

        this.themeService.register({
            id: PANEL_DARK_ID,
            label: 'LVGL Dark',
            description: 'Dark theme based on Theia Dark with LVGL branding',
            editorTheme: 'vs-dark',
            type: 'dark',
            activate(): void {
                document.body.classList.add('lvgl-dark');
                document.body.classList.add('vscode-dark');
                document.body.setAttribute('data-theme', 'dark');
            },
            deactivate(): void {
                document.body.classList.remove('lvgl-dark');
                document.body.classList.remove('vscode-dark');
                if (document.body.getAttribute('data-theme') === 'dark') {
                    document.body.removeAttribute('data-theme');
                }
            }
        });

        this.themeService.register({
            id: PANEL_LIGHT_ID,
            label: 'LVGL Light',
            description: 'Light theme based on Theia Light with LVGL branding',
            editorTheme: 'vs',
            type: 'light',
            activate(): void {
                document.body.classList.add('lvgl-light');
                document.body.classList.add('vscode-light');
                document.body.setAttribute('data-theme', 'light');
            },
            deactivate(): void {
                document.body.classList.remove('lvgl-light');
                document.body.classList.remove('vscode-light');
                if (document.body.getAttribute('data-theme') === 'light') {
                    document.body.removeAttribute('data-theme');
                }
            }
        });

        // Keep only LVGL themes in the picker.
        const internalThemes: Record<string, unknown> = (
            this.themeService as unknown as { themes: Record<string, unknown> }
        ).themes;
        for (const id of ['dark', 'light', 'hc-theia', 'hc-theia-light']) {
            delete internalThemes[id];
        }

        const currentTheme: string = this.themeService.getCurrentTheme().id;
        if (currentTheme !== PANEL_DARK_ID && currentTheme !== PANEL_LIGHT_ID) {
            this.themeService.setCurrentTheme(PANEL_DARK_ID, false);
        }

        if (this.iconThemeService.current !== PANEL_FILE_ICON_THEME_ID) {
            this.iconThemeService.current = PANEL_FILE_ICON_THEME_ID;
        }
    }

    onDidInitializeLayout(): void {
        document.body.classList.add('lvgl-theme');
        void this.closeOpenEditorsWidget();
    }

    protected patchNavigatorTabBarDecorator(): void {
        if (this.navigatorDecoratorPatched) return;

        try {
            const module = require('@theia/navigator/lib/browser/navigator-tab-bar-decorator') as {
                NavigatorTabBarDecorator?: { prototype?: Record<string, unknown> };
            };
            const prototype = module.NavigatorTabBarDecorator?.prototype;
            if (!prototype) return;

            const original = prototype.getDirtyEditorsCount;
            if (typeof original !== 'function') return;

            prototype.getDirtyEditorsCount = function patchedGetDirtyEditorsCount(this: {
                applicationShell?: { widgets?: unknown[] };
            }): number {
                const widgets = this.applicationShell?.widgets;
                if (!Array.isArray(widgets)) return 0;

                return widgets.filter((widget) => Saveable.isDirty(widget)).length;
            };

            this.navigatorDecoratorPatched = true;
        } catch {
            // No-op: this keeps startup resilient if module resolution changes.
        }
    }

    protected installCanceledRejectionHandler(): void {
        if (this.canceledRejectionHandlerInstalled) return;

        window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
            const reason: unknown = event.reason;
            const message: string = reason instanceof Error ? reason.message : String(reason ?? '');
            if (message.toLowerCase().includes('canceled')) {
                event.preventDefault();
            }
        });

        this.canceledRejectionHandlerInstalled = true;
    }

    protected installInvalidKeydownGuard(): void {
        if (this.invalidKeydownGuardInstalled) return;

        document.addEventListener(
            'keydown',
            (event: Event) => {
                if (event instanceof KeyboardEvent) return;

                event.stopImmediatePropagation();
                if (event.cancelable) event.preventDefault();
            },
            true
        );

        this.invalidKeydownGuardInstalled = true;
    }

    protected async closeOpenEditorsWidget(): Promise<void> {
        const widget = await this.widgetManager.getWidget(OPEN_EDITORS_WIDGET_ID);
        widget?.close();
    }
}
